import { Symbol } from '../../../models/symbol.model';
import { MarketViewMessage } from '../../market/services/market-view.model';

export interface IDepthData {
	bestBuyLimitPrice?: number;
	bestSellLimitPrice?: number;
	bestBuyLimitQuantity?: number;
	bestSellLimitQuantity?: number;
}

/** Maps a `MarketViewMessage` SSE tick to the 3-level depth ladder (protocol V4 sends no more). */
export function mapMarketViewMessageToDepthValues(message: MarketViewMessage): IDepthData[] {
	return [
		{
			bestBuyLimitPrice: message.bp1,
			bestBuyLimitQuantity: message.bv1,
			bestSellLimitPrice: message.ap1,
			bestSellLimitQuantity: message.av1
		},
		{
			bestBuyLimitPrice: message.bp2,
			bestBuyLimitQuantity: message.bv2,
			bestSellLimitPrice: message.ap2,
			bestSellLimitQuantity: message.av2
		},
		{
			bestBuyLimitPrice: message.bp3,
			bestBuyLimitQuantity: message.bv3,
			bestSellLimitPrice: message.ap3,
			bestSellLimitQuantity: message.av3
		}
	];
}

/**
 * REST snapshot of the depth ladder (top 3 levels), sourced from the symbol's own bid/ask book.
 * Used as the pre-SSE / disconnected fallback so the ladder never renders empty while the
 * live market-view stream is still connecting (or has dropped).
 */
export function mapSymbolOrderBookToDepthValues(symbol: Pick<Symbol, 'bids' | 'asks'> | undefined): IDepthData[] {
	if (!symbol) {
		return [];
	}

	return [0, 1, 2].map(level => ({
		bestBuyLimitPrice: symbol.bids[level]?.price,
		bestBuyLimitQuantity: symbol.bids[level]?.volume,
		bestSellLimitPrice: symbol.asks[level]?.price,
		bestSellLimitQuantity: symbol.asks[level]?.volume
	}));
}

/**
 * VWAP-style closing price from a live tick alone — 0 when the tick carries no
 * volume of its own.
 *
 * `tval`/`tv` are cumulative-session totals, not per-trade — `tval` is denominated in
 * thousand-rials while `tv` (volume) and prices are in rials, hence the `* 1000` to bring
 * the ratio back to rial units.
 *
 * 0 rather than a fallback, because a tick that carries no `tv` says nothing
 * about قیمت پایانی — it is not the same case as "no tick yet", and the caller
 * is the one that knows what to stand in: the snapshot's `closingPrice` is already
 * known, so answering with yesterday's settlement here would overwrite
 * today's VWAP with a staler figure.
 */
export function mapMarketViewTickToClosingPrice(live: MarketViewMessage | undefined): number {
	return live?.tv ? (live.tval / live.tv) * 1000 : 0;
}

/**
 * قیمت پایانی off the snapshot alone, for screens that have a `market-view` answer but no
 * tick yet — the portfolio list seeds every holding's قیمت پایانی this way, so مبنای قیمت
 * has a figure to work with on first paint rather than after the first tick.
 *
 * The backend's own `closingPrice`, which it holds at the last known figure after the
 * close and overnight (see `toSymbol` for the fallback when it is missing).
 */
export function mapSymbolToClosingPrice(meta: Pick<Symbol, 'closingPrice'> | undefined): number {
	return meta?.closingPrice ?? 0;
}

/**
 * قیمت پایانی for a screen holding both a live tick and the `market-view` snapshot:
 * the tick's own VWAP while it carries volume, and the snapshot's otherwise.
 *
 * The invariant this exists to hold: a certificate has one قیمت پایانی, whichever
 * screen is asking. The portfolio list seeds every holding from
 * {@link mapSymbolToClosingPrice}, so a screen that fell back to
 * `lastSettlementPrice` instead would value the same holding at yesterday's close
 * while the list valued it at today's VWAP — and under مبنای قیمت = قیمت پایانی
 * that is ارزش گواهی and سود و زیان, not just a printed row.
 */
export function mapToSessionClosingPrice(
	live: MarketViewMessage | undefined,
	meta: Pick<Symbol, 'closingPrice'> | undefined
): number {
	return mapMarketViewTickToClosingPrice(live) || mapSymbolToClosingPrice(meta);
}
